// Path: ./src/utils/banner/bannerFields.ts
import {
  BannerPlacement,
  BannerStatus,
  CreateGfBannerDto,
  GfBanner,
} from "@swagger/typescript-fetch-goflyer";

export const enumWireValues = (enumObject: object): string[] =>
  Object.entries(enumObject)
    .filter(([key]) => /^[A-Z]/.test(key))
    .map(([, value]) => String(value));

/**
 * Placements the admin currently offers. The API still accepts the other
 * BannerPlacement values, so keep any placement already saved on a banner
 * selectable when editing it.
 */
export const SUPPORTED_BANNER_PLACEMENTS: string[] = [
  String(BannerPlacement.FlyersHomeHero),
  String(BannerPlacement.DealsHomeHero),
];

export const bannerPlacementValues = (currentPlacement?: string): string[] =>
  currentPlacement && !SUPPORTED_BANNER_PLACEMENTS.includes(currentPlacement)
    ? [...SUPPORTED_BANNER_PLACEMENTS, currentPlacement]
    : SUPPORTED_BANNER_PLACEMENTS;

export const BANNER_CONTENT_FIELDS = [
  "eyebrow",
  "headline",
  "titleBadge",
  "subtitle",
  "cta",
  "footnote",
] as const;

export type BannerContentField = (typeof BANNER_CONTENT_FIELDS)[number];

export const BANNER_LOCALE_SUFFIXES = ["", "Chinese", "French"] as const;

export type BannerLocaleSuffix = (typeof BANNER_LOCALE_SUFFIXES)[number];

export type BannerContentFieldKey =
  `${BannerContentField}${BannerLocaleSuffix}`;

/**
 * Base (English / Chinese) caps. Must stay in step with
 * `BANNER_CONTENT_MAX_LENGTH` in goflyer-server/src/gf-banner/gf-banner.enum.ts —
 * the server rejects anything longer, so a looser counter here would let an
 * operator type a value that cannot be saved.
 */
export const BANNER_FIELD_LIMITS: Record<BannerContentField, number> = {
  eyebrow: 24,
  headline: 40,
  titleBadge: 24,
  subtitle: 80,
  cta: 24,
  footnote: 32,
};

/**
 * French needs materially more room than English for the same message (~30-40%
 * longer), which is why the shared cap made French copy unwritable. Mirrors
 * `BANNER_CONTENT_MAX_LENGTH_FRENCH` on the server.
 */
export const BANNER_FIELD_LIMITS_FRENCH: Record<BannerContentField, number> = {
  eyebrow: 36,
  headline: 60,
  titleBadge: 36,
  subtitle: 120,
  cta: 36,
  footnote: 48,
};

/**
 * The cap that applies to one field in one locale. Chinese shares the base
 * budget because CJK carries more meaning per character.
 */
export function bannerFieldLimit(
  field: BannerContentField,
  suffix: BannerLocaleSuffix,
): number {
  return suffix === "French"
    ? BANNER_FIELD_LIMITS_FRENCH[field]
    : BANNER_FIELD_LIMITS[field];
}

export const BANNER_REQUIRED_FIELDS = [
  "name",
  "placement",
  "desktopImageId",
] as const;

export const BANNER_MAX_TARGET_RADIUS_KM = 500;

export const bannerContentField = (
  base: BannerContentField,
  suffix: BannerLocaleSuffix,
): BannerContentFieldKey => `${base}${suffix}` as BannerContentFieldKey;

export type BannerDisplayState =
  "draft" | "paused" | "archived" | "scheduled" | "expired" | "live";

const toDate = (value?: Date | string): Date | undefined => {
  if (!value) return undefined;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export const getBannerDisplayState = (
  banner: Pick<GfBanner, "status" | "validStartDate" | "validEndDate">,
  now: Date = new Date(),
): BannerDisplayState => {
  if (String(banner.status) !== String(BannerStatus.Active)) {
    return String(banner.status) as BannerDisplayState;
  }

  const start = toDate(banner.validStartDate);
  if (start && start.getTime() > now.getTime()) return "scheduled";

  const end = toDate(banner.validEndDate);
  if (end && end.getTime() < now.getTime()) return "expired";

  return "live";
};

export type BannerSlotSize = "masthead" | "strip";

export const getBannerSlotSize = (placement?: string): BannerSlotSize =>
  String(placement ?? "").startsWith("dealsHome") ? "masthead" : "strip";

export const isGeoTargeted = (
  banner: Pick<CreateGfBannerDto, "targetCenter">,
): boolean => Boolean(banner.targetCenter);
